import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChevronLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    AlertTriangle,
    Clock,
    CheckCircle2,
    Mailbox,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { listTenders, listCommunications } from "@/lib/api";
import { fmtDate, fmtEUR, daysUntil, statusLabel } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

const MONTH_NAMES = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
];
const WEEK_DAYS = ["L", "M", "X", "J", "V", "S", "D"];

const ymd = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

const buildMonthGrid = (year, month) => {
    // Mon-first grid. month is 0-indexed.
    const firstDay = new Date(year, month, 1);
    const offset = (firstDay.getDay() + 6) % 7; // shift sunday
    const start = new Date(year, month, 1 - offset);
    const cells = [];
    for (let i = 0; i < 42; i++) {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        cells.push(d);
    }
    return cells;
};

export default function Calendar() {
    const navigate = useNavigate();
    const [tenders, setTenders] = useState([]);
    const [communications, setCommunications] = useState([]);
    const [loading, setLoading] = useState(true);
    const today = useMemo(() => new Date(), []);
    const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

    useEffect(() => {
        (async () => {
            try {
                const [t, c] = await Promise.all([listTenders(), listCommunications()]);
                setTenders(t.filter((x) => x.deadline));
                setCommunications(
                    (c.communications || []).filter(
                        (x) => x.extracted_deadline_date
                    )
                );
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // Unified events array: tender deadlines + communication AI-extracted deadlines.
    // Each event: {kind:'tender'|'comm', date, title, tender_id, ...}
    const events = useMemo(() => {
        const out = [];
        tenders.forEach((t) => {
            const date = (t.deadline || "").slice(0, 10);
            if (!date) return;
            out.push({
                kind: "tender",
                date,
                id: t.id,
                title: t.title,
                tender_id: t.id,
                _raw: t,
            });
        });
        communications.forEach((c) => {
            out.push({
                kind: "comm",
                date: c.extracted_deadline_date,
                id: c.id,
                title: c.extracted_deadline_label || c.title,
                tender_id: c.tender_id,
                _raw: c,
            });
        });
        return out;
    }, [tenders, communications]);

    const byDate = useMemo(() => {
        const map = {};
        events.forEach((e) => {
            if (!e.date) return;
            (map[e.date] = map[e.date] || []).push(e);
        });
        return map;
    }, [events]);

    const grid = useMemo(
        () => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()),
        [cursor]
    );

    const upcoming = useMemo(() => {
        const arr = events
            .map((e) => ({ ...e, _d: daysUntil(e.date) }))
            .filter((e) => e._d !== null && e._d >= -30)
            .sort((a, b) => a._d - b._d);
        return arr.slice(0, 12);
    }, [events]);

    const prevMonth = () =>
        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    const nextMonth = () =>
        setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

    return (
        <Layout>
            <div className="max-w-[1400px] mx-auto p-6 md:p-8" data-testid="calendar-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-600 mb-2">
                            — Vista temporal
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight font-black text-ink-950 leading-[1.05]">
                            Calendario
                        </h1>
                        <p className="text-base text-ink-500 mt-2 font-medium">
                            Plazos de presentación y vencimientos en una sola vista.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            className="rounded-full border-ink-200 h-10 px-4 font-bold"
                            onClick={goToday}
                            data-testid="cal-today-btn"
                        >
                            Hoy
                        </Button>
                        <div className="flex items-center gap-1 bg-white border border-ink-100 rounded-full p-1 shadow-pop">
                            <button
                                onClick={prevMonth}
                                className="w-9 h-9 rounded-full hover:bg-brand-50 text-ink-700 flex items-center justify-center transition-colors"
                                data-testid="cal-prev-btn"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <div className="px-3 font-display font-bold text-ink-950 min-w-[160px] text-center">
                                {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
                            </div>
                            <button
                                onClick={nextMonth}
                                className="w-9 h-9 rounded-full hover:bg-brand-50 text-ink-700 flex items-center justify-center transition-colors"
                                data-testid="cal-next-btn"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid lg:grid-cols-[1fr_380px] gap-6">
                    {/* Calendar grid */}
                    <div className="bg-white rounded-2xl border border-ink-100 shadow-pop overflow-hidden">
                        {/* Weekdays */}
                        <div className="grid grid-cols-7 border-b border-ink-100">
                            {WEEK_DAYS.map((w) => (
                                <div
                                    key={w}
                                    className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-400 text-center py-3"
                                >
                                    {w}
                                </div>
                            ))}
                        </div>
                        {/* Days */}
                        <div className="grid grid-cols-7">
                            {grid.map((d) => {
                                const isCurMonth = d.getMonth() === cursor.getMonth();
                                const isToday = ymd(d) === ymd(today);
                                const items = byDate[ymd(d)] || [];
                                return (
                                    <div
                                        key={ymd(d)}
                                        className={`min-h-[100px] border-b border-r border-ink-100 last:border-r-0 p-2 transition-colors ${
                                            isCurMonth ? "bg-white" : "bg-ink-50/50"
                                        } ${items.length > 0 ? "hover:bg-brand-50/40" : ""}`}
                                        data-testid={`cal-day-${ymd(d)}`}
                                    >
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span
                                                className={`text-xs font-bold w-7 h-7 inline-flex items-center justify-center rounded-full ${
                                                    isToday
                                                        ? "bg-brand-600 text-white shadow-pop"
                                                        : isCurMonth
                                                          ? "text-ink-950"
                                                          : "text-ink-300"
                                                }`}
                                            >
                                                {d.getDate()}
                                            </span>
                                            {items.length > 0 && (
                                                <span className="text-[10px] font-bold text-brand-600 bg-brand-50 px-1.5 rounded-full">
                                                    {items.length}
                                                </span>
                                            )}
                                        </div>
                                        <div className="space-y-1">
                                            {items.slice(0, 2).map((t) => {
                                                const dd = daysUntil(t.date);
                                                const urgent = dd !== null && dd >= 0 && dd < 7;
                                                const past = dd !== null && dd < 0;
                                                const isComm = t.kind === "comm";
                                                return (
                                                    <button
                                                        key={t.id}
                                                        onClick={() => {
                                                            if (isComm) {
                                                                navigate("/app/comunicaciones");
                                                            } else {
                                                                navigate(`/app/licitacion/${t.id}`);
                                                            }
                                                        }}
                                                        className={`w-full text-left text-[11px] font-semibold leading-tight px-2 py-1 rounded-md truncate transition-colors inline-flex items-center gap-1 ${
                                                            urgent
                                                                ? "bg-red-50 text-red-700 hover:bg-red-100"
                                                                : past
                                                                  ? "bg-ink-100 text-ink-400 hover:bg-ink-200 line-through"
                                                                  : isComm
                                                                    ? "bg-violet-50 text-violet-700 hover:bg-violet-100"
                                                                    : "bg-brand-50 text-brand-700 hover:bg-brand-100"
                                                        }`}
                                                        data-testid={`cal-event-${t.id}`}
                                                        title={t.title}
                                                    >
                                                        {isComm && <Mailbox size={9} className="shrink-0" />}
                                                        <span className="truncate">{t.title}</span>
                                                    </button>
                                                );
                                            })}
                                            {items.length > 2 && (
                                                <div className="text-[10px] text-ink-400 px-2 font-bold">
                                                    +{items.length - 2} más
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Upcoming list */}
                    <div className="bg-white rounded-2xl border border-ink-100 shadow-pop p-6">
                        <div className="flex items-center gap-2 mb-5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center shadow-pop">
                                <CalendarIcon size={16} />
                            </div>
                            <div>
                                <div className="font-display font-bold text-ink-950">
                                    Próximos vencimientos
                                </div>
                                <div className="text-xs font-medium text-ink-400">
                                    Ordenado por proximidad
                                </div>
                            </div>
                        </div>
                        {loading ? (
                            <div className="text-sm text-ink-400">Cargando…</div>
                        ) : upcoming.length === 0 ? (
                            <div className="py-10 text-center">
                                <CheckCircle2
                                    className="mx-auto text-emerald-500 mb-3"
                                    size={32}
                                />
                                <div className="text-sm font-bold text-ink-950">
                                    Sin vencimientos cercanos
                                </div>
                                <div className="text-xs text-ink-400 mt-1">
                                    Añade fechas límite a tus licitaciones para verlas aquí.
                                </div>
                            </div>
                        ) : (
                            <ul className="space-y-2.5">
                                {upcoming.map((t) => {
                                    const dd = t._d;
                                    const urgent = dd !== null && dd >= 0 && dd < 7;
                                    const past = dd !== null && dd < 0;
                                    const isComm = t.kind === "comm";
                                    return (
                                        <li
                                            key={t.id}
                                            onClick={() => {
                                                if (isComm) {
                                                    navigate("/app/comunicaciones");
                                                } else {
                                                    navigate(`/app/licitacion/${t.id}`);
                                                }
                                            }}
                                            className="cursor-pointer group p-3 rounded-xl border border-ink-100 hover:border-brand-300 hover:shadow-pop transition-all"
                                            data-testid={`upcoming-${t.id}`}
                                        >
                                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                                <div className="font-bold text-sm text-ink-950 group-hover:text-brand-700 line-clamp-2 inline-flex items-center gap-1.5">
                                                    {isComm && (
                                                        <Mailbox
                                                            size={13}
                                                            className="text-violet-600 shrink-0"
                                                        />
                                                    )}
                                                    {t.title}
                                                </div>
                                                {urgent && (
                                                    <AlertTriangle
                                                        size={14}
                                                        className="text-red-500 shrink-0 mt-0.5"
                                                    />
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 text-xs">
                                                <Clock size={11} className="text-ink-400" />
                                                <span className="text-ink-500 font-medium">
                                                    {fmtDate(t.date)}
                                                </span>
                                                <span
                                                    className={`font-bold ${
                                                        urgent
                                                            ? "text-red-600"
                                                            : past
                                                              ? "text-ink-400"
                                                              : isComm
                                                                ? "text-violet-600"
                                                                : "text-brand-600"
                                                    }`}
                                                >
                                                    ·{" "}
                                                    {past
                                                        ? `Hace ${Math.abs(dd)} días`
                                                        : dd === 0
                                                          ? "Hoy"
                                                          : `${dd} días`}
                                                </span>
                                            </div>
                                            {isComm && t._raw?.tender_title && (
                                                <div className="text-[11px] font-bold text-ink-500 mt-1 truncate">
                                                    Licitación: {t._raw.tender_title}
                                                </div>
                                            )}
                                            {!isComm && t._raw?.budget && (
                                                <div className="text-xs font-mono text-ink-400 mt-1">
                                                    {fmtEUR(t._raw.budget)}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
